import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import blackListDictionary from './spam-blacklist-dictionary.json';

export class SpamValidators {
  /**
   * Validator function to check if the control's value contains any blacklisted words.
   *
   * @param control - The form control to validate.
   * @returns A validation error object with the key `blackList` set to `true`
   * if any blacklisted words are found, otherwise `null`.
   */
  static readonly checkBlackListWords: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const words = control.value.toLowerCase().split(' ');
    const wordsFounded = blackListDictionary.single_words.filter((word: string) => words.includes(word));
    const phrasesFounded = blackListDictionary.phrases.filter((phrases: string) => control.value.includes(phrases));
    const elmentsFounded = [...wordsFounded, ...phrasesFounded].filter(
      (value, index, array) => array.indexOf(value) === index
    );
    return elmentsFounded.length > 0
      ? {
          blackList: elmentsFounded.join(', '),
        }
      : null;
  };

  /**
   * Validator function to check if the control's value contains a link.
   *
   * This validator checks if the value of the control contains a URL pattern.
   * If a URL is found, it returns an object with the key `link` set to `true`.
   * Otherwise, it returns `null`.
   *
   * @returns A `ValidatorFn` that checks for links in the control's value.
   */
  static readonly checkLinks: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    return /:\/\//.test(control.value) ? { link: true } : null;
  };

  /**
   * Validator that checks if the control's value contains only spekable characters and spaces.
   *
   * @returns A validator function that returns a validation error object with the key `onlyAlphabets` set to `true`
   * if the control's value contains non-alphabetic characters, or `null` if the value is valid.
   */
  static readonly spekableCharacters: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    return /^[\p{L}\s]+$/u.test(control.value) ? null : { onlyAlphabets: true };
  };

  /**
   * Validator that checks if the number of words in the control's value does not exceed the specified maximum.
   *
   * @param max - The maximum number of words allowed.
   * @returns A validator function that returns a validation error if the number of words exceeds
   * the maximum, otherwise null.
   */
  static maxWords(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const words = control.value.split(' ');
      return words.length <= max ? null : { maxWords: max };
    };
  }
}
